import { useMutation, useQuery } from '@apollo/client';
import { ButtonDock, miniButtonDockScrollBottomPad, translateTemplateText } from '@focuslee/shared-mini-app';
import { Box, Button, Stack } from '@mui/material';
import { Cell, Chip, Section, Text } from '@telegram-apps/telegram-ui';
import { hapticFeedback } from '@tma.js/sdk';
import { motion } from 'framer-motion';
import { type FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import dayjs from 'dayjs';
import { CreateAchievementMutation, CreateAchievementMutationVariables, CreateCategoryMutation, CreateCategoryMutationVariables, CreateQuestMutation, CreateQuestMutationVariables, GetTemplateAchievementsQuery, GetTemplatesQuery, QuestDifficulty, RepeatCreateInput, RepeatingType, UpdateAchievementConditionsMutation, UpdateAchievementConditionsMutationVariables } from '../client/generated/graphql';
import { createAchievementCache, createAchievementMutation } from '../client/mutations/createAchievement';
import { createCategoryCache, createCategoryMutation } from '../client/mutations/createCategory';
import { createQuestCache, createQuestMutation } from '../client/mutations/createQuest';
import { updateAchievementConditionsMutation } from '../client/mutations/updateAchievementConditions';
import { getTemplatesQuery } from '../client/queries/template';
import { getTemplateAchievementsQuery } from '../client/queries/templateAchievements';
import { Page } from '../components/Page';
import { AchievementItem } from '../components/achievements/AchievementItem';
import { CommonIcon } from '../components/common/CommonIcon';
import { useCachedCategories } from '../hooks/caching/category';

const REGULARITY_OPTIONS = [
    { id: 'daily', label: 'Every Day', type: RepeatingType.Day, interval: 1 },
    { id: 'weekly', label: 'Every Week', type: RepeatingType.Week, interval: 1 },
    { id: 'monthly', label: 'Every Month', type: RepeatingType.Month, interval: 1 },
];

const TIMING_OPTIONS = [
    { id: '15', label: '15 minutes', value: 15 },
    { id: '30', label: '30 minutes', value: 30 },
    { id: '60', label: '1 hour', value: 60 },
    { id: '120', label: '2 hours', value: 120 },
];

export const TemplateConfigPage: FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { templateId } = useParams<{ templateId: string }>();
    const [searchParams] = useSearchParams();
    const selectedDate = searchParams.get('selectedDate');
    const { loading, data } = useQuery<GetTemplatesQuery>(getTemplatesQuery);
    const { data: templateAchievementsData } = useQuery<GetTemplateAchievementsQuery>(getTemplateAchievementsQuery);

    const [selectedRegularity, setSelectedRegularity] = useState<string>('daily');
    const [selectedTiming, setSelectedTiming] = useState<string>('15');
    const [selectedAchievements, setSelectedAchievements] = useState<Set<string>>(new Set());

    const [createQuest, { loading: isCreateLoading }] = useMutation<CreateQuestMutation, CreateQuestMutationVariables>(createQuestMutation, {
        update: createQuestCache
    });

    const [createCategory, { loading: isCreateCategoryLoading }] = useMutation<CreateCategoryMutation, CreateCategoryMutationVariables>(createCategoryMutation, {
        update: createCategoryCache
    });

    const [createAchievement, { loading: isCreateAchievementLoading }] = useMutation<CreateAchievementMutation, CreateAchievementMutationVariables>(createAchievementMutation, {
        update: createAchievementCache
    });

    const [updateAchievementConditions, { loading: isUpdateAchievementConditionsLoading }] = useMutation<UpdateAchievementConditionsMutation, UpdateAchievementConditionsMutationVariables>(updateAchievementConditionsMutation);

    const cachedCategories = useCachedCategories();

    const templateData = data?.templateCategories.find(cat => 
        cat.quests.some(quest => quest.id === templateId)
    );
    const templateQuest = templateData?.quests.find(quest => quest.id === templateId);
    const templateCategory = templateData;
    const templateQuestName = templateQuest ? translateTemplateText(t, templateQuest.id, 'name', templateQuest.name) : '';
    const templateQuestDescription = templateQuest ? translateTemplateText(t, templateQuest.id, 'description', templateQuest.description ?? '') : '';

    const relatedAchievements = useMemo(() => {
        if (!templateCategory || !templateQuest) return [];
        return templateCategory.achievements.filter(achievement => 
            achievement.questIds.includes(templateQuest.id)
        );
    }, [templateCategory, templateQuest]);

    // Map template achievement IDs to existing achievements
    const existingAchievementsMap = useMemo(() => {
        const map = new Map();
        if (templateAchievementsData?.achievements) {
            templateAchievementsData.achievements.forEach(achievement => {
                if (achievement.templateId) {
                    map.set(achievement.templateId, achievement);
                }
            });
        }
        return map;
    }, [templateAchievementsData]);

    const navigateBack = useCallback(() => {
        navigate(-1);
    }, [navigate]);

    const handleAchievementToggle = useCallback((achievementId: string) => {
        hapticFeedback.impactOccurred.ifAvailable('light');
        setSelectedAchievements(prev => {
            const newSet = new Set(prev);
            if (newSet.has(achievementId)) {
                newSet.delete(achievementId);
            } else {
                newSet.add(achievementId);
            }
            return newSet;
        });
    }, []);

    const handleSelectAll = useCallback(() => {
        hapticFeedback.impactOccurred.ifAvailable('medium');
        if (selectedAchievements.size === relatedAchievements.length) {
            setSelectedAchievements(new Set());
        } else {
            setSelectedAchievements(new Set(relatedAchievements.map(a => a.id)));
        }
    }, [selectedAchievements.size, relatedAchievements]);

    const handleCreateQuest = useCallback(async () => {
        if (!templateQuest || !templateCategory || isCreateLoading || isCreateCategoryLoading || isCreateAchievementLoading || isUpdateAchievementConditionsLoading) return;

        try {
            const existingCategory = cachedCategories.find(cat => cat.templateId === templateCategory.id);
            let categoryId: string | null = existingCategory ? existingCategory.id : null;

            if (!categoryId) {
                const categoryResult = await createCategory({
                    variables: {
                        name: templateCategory.name,
                        icon: templateCategory.icon,
                        iconColor: templateCategory.iconColor,
                        parentId: null,
                        goalEnabled: false,
                        templateId: templateCategory.id,
                    }
                });
                
                categoryId = categoryResult?.data?.createCategory?.category?.id!;
            }

            let repeat: RepeatCreateInput | null = null;
            if (templateQuest.repeat && selectedRegularity) {
                const regularityOption = REGULARITY_OPTIONS.find(opt => opt.id === selectedRegularity);
                if (regularityOption) {
                    repeat = {
                        repeatingType: regularityOption.type,
                        interval: regularityOption.interval,
                        query: regularityOption.label.toLowerCase(),
                        weekDays: [],
                        monthDays: [],
                        startHour: null,
                        startMinute: null,
                    };
                }
            }

            const timingOption = templateQuest.timing && selectedTiming ? TIMING_OPTIONS.find(opt => opt.id === selectedTiming) : null;
            const timing = templateQuest.timing && !!timingOption;
            const timedInterval = timingOption?.value || templateQuest.timedInterval || 0;
            const questResult = await createQuest({
                variables: {
                    name: templateQuest.name,
                    description: templateQuest.description || '',
                    categoryId,
                    difficulty: templateQuest.difficulty as QuestDifficulty,
                    repeat,
                    dueDate: selectedDate ? dayjs(selectedDate).startOf('day').toDate() : null,
                    timing: timing || false,
                    timedInterval,
                    timerValueInMs: 0,
                    goalId: null,
                    templateId: templateQuest.id,
                }
            });

            const createdQuestId = questResult?.data?.createQuest?.id;

            if (createdQuestId && selectedAchievements.size > 0) {
                const achievementsToProcess = relatedAchievements.filter(a => selectedAchievements.has(a.id));
                
                for (const templateAchievement of achievementsToProcess) {
                    const existingAchievement = existingAchievementsMap.get(templateAchievement.id);
                    
                    if (existingAchievement) {
                        // Update existing achievement by adding the new quest ID
                        const updatedAchievementConditions = existingAchievement.achievementConditions.map((c: any) => ({
                            metricType: c.metricType,
                            target: c.target,
                            questIds: [...c.questIds, createdQuestId]
                        }));

                        await updateAchievementConditions({
                            variables: {
                                achievementId: existingAchievement.id,
                                achievementConditions: updatedAchievementConditions,
                            }
                        });
                    } else {
                        // Create new achievement
                        const mappedAchievementConditions = templateAchievement.achievementConditions.map(c => ({
                            metricType: c.metricType,
                            target: c.target,
                            questIds: [createdQuestId]
                        }));

                        await createAchievement({
                            variables: {
                                name: templateAchievement.name,
                                description: templateAchievement.description,
                                categoryId,
                                rarity: templateAchievement.rarity,
                                icon: templateAchievement.icon,
                                iconImageColor: templateAchievement.iconImageColor,
                                iconImageUrl: templateAchievement.iconImageUrl,
                                iconImageUrlNotAchieved: templateAchievement.iconImageUrlNotAchieved,
                                achievementConditions: mappedAchievementConditions,
                                templateId: templateAchievement.id,
                            }
                        });
                    }
                }
            }

            navigate(-2);
        } catch (error) {
            console.error("Error creating quest from template:", error);
        }
    }, [templateQuest, templateCategory, selectedRegularity, selectedTiming, selectedDate, selectedAchievements, relatedAchievements, existingAchievementsMap, createQuest, createCategory, createAchievement, updateAchievementConditions, isCreateLoading, isCreateCategoryLoading, isCreateAchievementLoading, isUpdateAchievementConditionsLoading, cachedCategories, navigate]);

    const isLoading = isCreateLoading || isCreateCategoryLoading || isCreateAchievementLoading || isUpdateAchievementConditionsLoading;

    if (loading || !templateQuest || !templateCategory) {
        return (
            <Page back={true}>
                <Text>Loading template...</Text>
            </Page>
        );
    }

    const hasRepeat = templateQuest.repeat !== null;
    const hasTiming = templateQuest.timing === true;

    return (
        <Page
            back={true}
            insetEnabled={false}
            footer={
                <ButtonDock
                    secondary={{ text: 'Back', onClick: navigateBack, enabled: !isLoading, navigation: 'back' }}
                    main={{ text: 'Create', onClick: handleCreateQuest, enabled: !isLoading, loading: isLoading }}
                />
            }
        >
            <Stack height="100%">
                <Stack alignItems="center" justifyContent="center" minHeight="var(--tg-viewport-content-safe-area-inset-top)">
                    <Text size={20}>Configure Template</Text>
                </Stack>
                <motion.div
                    key="config-content"
                    style={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.4 }}
                >
                    <Stack 
                        paddingLeft="10px" 
                        paddingRight="10px" 
                        paddingTop="4px" 
                        gap={2}
                        sx={{ flexShrink: 0 }}
                    >
                        {/* Blocked Name */}
                        <Box sx={{ 
                            padding: '10px 0',
                            borderBottom: '1px solid var(--tgui--section_separator_color)'
                        }}>
                            <Text style={{ 
                                fontSize: '20px', 
                                lineHeight: '20px',
                                color: 'var(--tgui--hint_color)'
                            }}>
                                {templateQuestName}
                            </Text>
                        </Box>

                        {/* Blocked Description */}
                        <Box sx={{ 
                            borderBottom: '1px solid var(--tgui--section_separator_color)'
                        }}>
                            <Text style={{ color: 'var(--tgui--hint_color)' }}>
                                {templateQuestDescription || t('achievementEditor.noDescription')}
                            </Text>
                        </Box>

                        {/* Regularity Section - only show if template has repeat */}
                        {hasRepeat && (
                            <Stack>
                                <Stack direction="row" alignItems="center" columnGap={1} sx={{ mb: 2 }}>
                                    <CommonIcon icon="repeat" type="filled" color="var(--tg-theme-text-color)" size={20} />
                                    <Text weight="2">Regularity</Text>
                                </Stack>
                                <Stack direction="row" gap={1} flexWrap="wrap">
                                    {REGULARITY_OPTIONS.map((option) => (
                                    <Chip
                                        key={option.id}
                                        mode={selectedRegularity === option.id ? "elevated" : "outline"}
                                        style={{ background: selectedRegularity === option.id ? 'var(--tg-theme-button-color)' : 'var(--tg-theme-secondary-bg-color)'}} 
                                        onClick={() => setSelectedRegularity(option.id)}
                                    >
                                        {option.label}
                                    </Chip>
                                    ))}
                                </Stack>
                            </Stack>
                        )}

                        {/* Timing Section - only show if template has timing */}
                        {hasTiming && (
                            <Stack>
                                <Stack direction="row" alignItems="center" columnGap={1} sx={{ mb: 2 }}>
                                    <CommonIcon icon="timer" type="filled" color="var(--tg-theme-text-color)" size={20} />
                                    <Text weight="2">Timing</Text>
                                </Stack>
                                <Stack direction="row" gap={1} flexWrap="wrap">
                                    {TIMING_OPTIONS.map((option) => (
                                    <Chip
                                        key={option.id}
                                        mode={selectedTiming === option.id ? "elevated" : "outline"}
                                        style={{ background: selectedTiming === option.id ? 'var(--tg-theme-button-color)' : 'var(--tg-theme-secondary-bg-color)'}} 
                                        onClick={() => setSelectedTiming(option.id)}
                                    >
                                        {option.label}
                                    </Chip>
                                    ))}
                                </Stack>
                            </Stack>
                        )}

                        {relatedAchievements.length > 0 && (
                            <Stack>
                                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                                    <Stack direction="row" alignItems="center" columnGap={1}>
                                        <CommonIcon icon="emoji_events" type="filled" color="var(--tg-theme-text-color)" size={20} />
                                        <Text weight="2">Achievements</Text>
                                        <Text style={{ color: 'var(--tgui--hint_color)', fontSize: '14px' }}>
                                            ({selectedAchievements.size}/{relatedAchievements.length} selected)
                                        </Text>
                                    </Stack>
                                    <Button
                                        onClick={handleSelectAll}
                                        size="small"
                                        sx={{
                                            textTransform: 'none',
                                            color: 'var(--tg-theme-button-color)',
                                            fontSize: '14px',
                                            padding: '4px 8px',
                                            minWidth: 'auto'
                                        }}
                                    >
                                        {selectedAchievements.size === relatedAchievements.length ? 'Deselect All' : 'Select All'}
                                    </Button>
                                </Stack>
                            </Stack>
                        )}
                    </Stack>

                    {relatedAchievements.length > 0 && (
                        <Box 
                            sx={{ 
                                flex: 1, 
                                overflowY: 'auto',
                                overflowX: 'hidden',
                                paddingLeft: "10px",
                                paddingRight: "10px",
                                paddingBottom: miniButtonDockScrollBottomPad,
                                minHeight: 0
                            }}
                        >
                            <Stack gap={1}>
                                        {relatedAchievements.map((achievement) => {
                                            const existingAchievement = existingAchievementsMap.get(achievement.id);
                                            const questCount = existingAchievement?.questIds?.length || 0;
                                            
                                            return (
                                                <Section key={achievement.id} style={{ padding: 0, margin: 0 }}>
                                                    <Cell
                                                        onClick={() => handleAchievementToggle(achievement.id)}
                                                        style={{
                                                            border: selectedAchievements.has(achievement.id) ? '2px solid var(--tg-theme-button-color)' : '2px solid transparent',
                                                            transition: 'all 0.2s ease'
                                                        }}
                                                        before={
                                                            <Box sx={{
                                                                width: 38,
                                                                height: 49
                                                            }}>
                                                                <AchievementItem
                                                                    icon={achievement.icon}
                                                                    title=""
                                                                    rarity={achievement.rarity}
                                                                    iconImageColor={achievement.iconImageColor}
                                                                    iconImageUrl={achievement.iconImageUrl}
                                                                />
                                                            </Box>
                                                        }
                                                        subtitle={
                                                            <Stack gap={0.5}>
                                                                <Text style={{ whiteSpace: selectedAchievements.has(achievement.id) ? 'pre-line' : 'nowrap',fontSize: '13px', color: 'var(--tgui--hint_color)' }}>
                                                                    {translateTemplateText(t, achievement.id, 'description', achievement.description ?? '')}
                                                                </Text>
                                                                {existingAchievement && (
                                                                    <Stack>
                                                                        <Stack direction="row" alignItems="center" gap={0.5}>
                                                                            <CommonIcon icon="check" type="filled" color="var(--tg-theme-button-color)" size={14} />
                                                                            <Text style={{ fontSize: '12px', color: 'var(--tgui--hint_color)' }}>
                                                                                Existing achievement · {questCount} {questCount === 1 ? 'quest' : 'quests'}
                                                                            </Text>
                                                                        </Stack>
                                                                        <Text style={{ fontSize: '12px', color: 'var(--tgui--hint_color)' }}>
                                                                            Your quest will be added
                                                                        </Text>
                                                                    </Stack>
                                                                )}
                                                            </Stack>
                                                        }
                                                    >
                                                        {translateTemplateText(t, achievement.id, 'name', achievement.name)}
                                                    </Cell>
                                                </Section>
                                            );
                                        })}
                            </Stack>
                        </Box>
                    )}
                </motion.div>
            </Stack>
        </Page>
    );
};
